"use client";

import { useEffect, useMemo, useState } from "react";
import { listGoals, type GoalItem } from "@/lib/api";
import { LIFE_AREA_META } from "@/lib/lifeAreas";

interface Props {
  itemTitle: string;
  currentGoalId: string | null;
  isBusy: boolean;
  onCancel: () => void;
  onSubmit: (goalId: string | null) => void;
}

export default function LibraryGoalPicker({
  itemTitle,
  currentGoalId,
  isBusy,
  onCancel,
  onSubmit,
}: Props) {
  const [goals, setGoals] = useState<GoalItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(currentGoalId);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await listGoals("active");
        if (!cancelled) setGoals(res.goals);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Could not load goals.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!goals) return [];
    if (!search.trim()) return goals;
    const q = search.trim().toLowerCase();
    return goals.filter((g) => g.title.toLowerCase().includes(q));
  }, [goals, search]);

  return (
    <div
      data-testid="library-goal-picker"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/25 backdrop-blur-[2px] px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="library-goal-picker-title"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-2 rounded-t-2xl bg-forest-med" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="rounded-full bg-sage-pale px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-forest-med">
                Link to goal
              </span>
              <h2 id="library-goal-picker-title" className="mt-2 text-lg font-semibold leading-snug text-text">
                {itemTitle}
              </h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search goals…"
            className="mt-4 w-full rounded-full border border-border bg-bg px-4 py-2 text-sm text-text placeholder:text-muted focus:border-forest-med/40 focus:outline-none"
          />

          <div className="mt-3 max-h-[320px] overflow-y-auto rounded-xl border border-border">
            <button
              type="button"
              data-testid="library-goal-option-none"
              onClick={() => setSelected(null)}
              className={`flex w-full items-center justify-between border-b border-border px-3 py-2.5 text-left text-sm transition-colors hover:bg-bg ${
                selected === null ? "bg-sage-pale/60" : ""
              }`}
            >
              <span className="text-text">None — keep unlinked</span>
              {selected === null && <span className="text-forest-med">✓</span>}
            </button>
            {goals === null && !loadError && (
              <div className="px-3 py-4 text-center text-sm text-muted">Loading goals…</div>
            )}
            {loadError && (
              <div className="px-3 py-4 text-center text-sm text-amber">{loadError}</div>
            )}
            {goals !== null && filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-muted">No matching goals.</div>
            )}
            {filtered.map((goal) => {
              const meta = goal.category ? LIFE_AREA_META[goal.category] : null;
              const isSelected = selected === goal.id;
              return (
                <button
                  key={goal.id}
                  type="button"
                  data-testid={`library-goal-option-${goal.id}`}
                  onClick={() => setSelected(goal.id)}
                  className={`flex w-full items-center justify-between border-b border-border px-3 py-2.5 text-left text-sm transition-colors hover:bg-bg ${
                    isSelected ? "bg-sage-pale/60" : ""
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {meta && (
                      <span className="mr-1.5 capitalize text-muted">
                        {meta.emoji} {meta.label}
                      </span>
                    )}
                    <span className="text-text">{goal.title}</span>
                  </span>
                  {isSelected && <span className="ml-2 text-forest-med">✓</span>}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              data-testid="library-goal-save"
              onClick={() => onSubmit(selected)}
              disabled={isBusy || selected === currentGoalId}
              className="flex-1 rounded-full bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
            >
              {isBusy ? "Linking…" : "Link"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isBusy}
              className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition-colors hover:border-forest-med/40 hover:text-forest-med disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
